import { useState } from 'react';
import { AdditionalInfoSearchDto, IdentificationDocSearchDto, PersonListDto, PhysicalFeaturesSearchDto, PhysicalInfoSearchDto } from '@app/api2/api';
import toast from 'react-hot-toast';
import {
  Form,
  Segment,
  Header,
  Grid,
  Divider,
  Message,
  Label,
  Icon,
  Button,
  Card,
  List
} from 'semantic-ui-react';

import { SEARCH_TYPES, BOOLEAN_OPERATORS, SEARCH_TYPES_NAMES } from './types';
import type { SearchCriteria, SearchCriteriaParamsType } from './types';
import { useSearchExecutor } from './useSearchExecutor';
import { SearchCriteriaCard } from './SearchCriteriaCard';
import { SearchResults } from './SearchResults';
import BiographicalForm from './components/forms/BiographicalForm';
import PhysicalForm from './components/forms/PhysicalForm';
import ContactForm from './components/forms/ContactForm';
import IdentificationForm from './components/forms/IdentificationForm';
import DnaForm from './components/forms/DnaForm';
import EmploymentForm from './components/forms/EmploymentForm';
import FeaturesForm from './components/forms/FeaturesForm';
import AliasesForm from './components/forms/AliasesForm';
import AdditionalInfoForm from './components/forms/AdditionalInfoForm';

export function SearchTab() {
  const [searchCriterias, setSearchCriterias] = useState<SearchCriteria[]>([]);
  const [currentSearchType, setCurrentSearchType] = useState<string>(SEARCH_TYPES_NAMES.BiographicalSearchDto);
  const [currentParams, setCurrentParams] = useState<SearchCriteriaParamsType>({ discriminator: SEARCH_TYPES_NAMES.BiographicalSearchDto });
  const [currentOperator, setCurrentOperator] = useState<'AND' | 'OR' | 'NOT'>('AND');
  const [searchResults, setSearchResults] = useState<PersonListDto[]>([]);
  const [activeAccordion, setActiveAccordion] = useState(-1);
  const [isAddingAdditionalFilters, setIsAddingAdditionalFilters] = useState(false);




  // DNA hook
  // const { selectedLociOrder, setSelectedLociOrder, dnaMarkers, updateAlleleValue, normalize, reset } = useDnaMarkers('ANDE FlexPlex');

  // search executor hook
  const { isSearching, setIsSearching, executeMultipleSearch } = useSearchExecutor();

  const addSearchCriteria = (values) => {
    const newCriteria: SearchCriteria = {
      id: Date.now().toString(),
      searchType: currentSearchType,
      params: { ...values, operator: currentOperator },
      operator: currentOperator
    };

    setSearchCriterias([...searchCriterias, newCriteria]);

    toast.success(`Criteria Added. Added ${currentSearchType} search criteria with ${currentOperator} operator`);
  };

  const removeCriteria = (id: string) => {
    setSearchCriterias(searchCriterias.filter(c => c.id !== id));
  };

  const clearAllFilters = () => {
    setSearchCriterias([]);
    setCurrentParams({ discriminator: SEARCH_TYPES_NAMES.BiographicalSearchDto });
    setSearchResults([]);
    setCurrentSearchType(SEARCH_TYPES_NAMES.BiographicalSearchDto);
    setCurrentOperator('AND');
    setActiveAccordion(-1);
    setIsAddingAdditionalFilters(false);
    // setSelectedLociOrder('ANDE FlexPlex');
    // reset();
    toast.success("Filters Cleared. All search filters have been cleared and the form has been reset.");
  };

  // const applyAdditionalFilters = () => {
  //   setIsAddingAdditionalFilters(true);
  //   const usedTypes = searchCriterias.map(criteria => criteria.searchType);
  //   const availableTypes = SEARCH_TYPES.filter(type => !usedTypes.includes(type.value));
  //   if (availableTypes.length > 0) {
  //     setCurrentSearchType(availableTypes[0].value);
  //     setCurrentParams({ discriminator: '' });
  //   }
  //   setTimeout(() => {
  //     const addCriteriaSection = document.querySelector('[data-testid="add-criteria-section"]');
  //     if (addCriteriaSection) {
  //       addCriteriaSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
  //     }
  //   }, 100);
  // };

  const getUsedSearchTypes = () => searchCriterias.map(criteria => criteria.searchType);

  const getAvailableSearchTypes = () => {
    if (!isAddingAdditionalFilters) return SEARCH_TYPES;
    const usedTypes = getUsedSearchTypes();
    return SEARCH_TYPES.filter(type => !usedTypes.includes(type.value));
  };

  const executeSearch = async (searchCriterias: Array<SearchCriteriaParamsType>) => {

    if (searchCriterias.length === 0) {
      toast.error("No Search Criteria: Please enter search criteria or add search filters");
      return;
    }

    setIsSearching(true);
    try {
      const allResults = await executeMultipleSearch(searchCriterias);
      setSearchResults(allResults);
      toast.success(`Search Complete. Found ${allResults.length} results matching your criteria`);
    } catch (error) {
      console.error('Search error:', error);
      toast.error(`Search Failed: ${error instanceof Error ? error.message : 'An error occurred during search'}`);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAccordionClick = (index: number) => {
    setActiveAccordion(activeAccordion === index ? -1 : index);
  };

  const handleSetCurrentParams = async (params: SearchCriteriaParamsType) => {
    if (params['__submitter'] === 'add-criteria') {
      params['__submitter'] = undefined;
      addSearchCriteria(params);
    } else {
      await executeSearch([params]);
    }
  }

  const renderSearchForm = () => {
    switch (currentSearchType) {
      case SEARCH_TYPES_NAMES.BiographicalSearchDto:
        return <BiographicalForm currentParams={currentParams} setCurrentParams={handleSetCurrentParams} />;
      case SEARCH_TYPES_NAMES.PhysicalInfoSearchDto:
        return <PhysicalForm currentParams={currentParams as PhysicalInfoSearchDto} setCurrentParams={handleSetCurrentParams} />;
      case SEARCH_TYPES_NAMES.ContactSearchDto:
        return <ContactForm currentParams={currentParams} setCurrentParams={handleSetCurrentParams} />;
      case SEARCH_TYPES_NAMES.IdentificationDocSearchDto:
        return <IdentificationForm currentParams={currentParams as IdentificationDocSearchDto} setCurrentParams={handleSetCurrentParams} />;
      case SEARCH_TYPES_NAMES.DnaProfileSearchDto:
        return (
          <DnaForm
            currentParams={currentParams}
            setCurrentParams={handleSetCurrentParams}
          />
        );
      case SEARCH_TYPES_NAMES.EmploymentSearchDto:
        return <EmploymentForm currentParams={currentParams} setCurrentParams={handleSetCurrentParams} />;
      case SEARCH_TYPES_NAMES.PhysicalFeaturesSearchDto:
        return <FeaturesForm currentParams={currentParams as PhysicalFeaturesSearchDto} setCurrentParams={handleSetCurrentParams} />;
      case SEARCH_TYPES_NAMES.AliasSearchDto:
        return <AliasesForm currentParams={currentParams} setCurrentParams={handleSetCurrentParams} />;
      case SEARCH_TYPES_NAMES.AdditionalInfoSearchDto:
        return <AdditionalInfoForm currentParams={currentParams as AdditionalInfoSearchDto} setCurrentParams={handleSetCurrentParams} />;
      default:
        return null;
    }
  };

  return (
    <div data-testid="search-tab">
      <Grid>
        <AdvancedHeader />

        {(searchCriterias.length > 0) && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Segment>
                <Header as="h3">
                  {/* <Icon name="filter" /> */}
                  Active Search Filters
                  <Label circular color="blue">{searchCriterias.length}</Label>
                </Header>

                <Card.Group itemsPerRow={2}>
                  {searchCriterias.map((criteria, index) => (
                    <SearchCriteriaCard
                      key={criteria.id}
                      criteria={criteria}
                      index={index}
                      onRemove={removeCriteria}
                      getTypeText={(v) => SEARCH_TYPES.find(t => t.value === v)?.text || v}
                    />
                  ))}
                </Card.Group>

                <Divider />

                <Button
                  primary
                  size="large"
                  onClick={() => executeSearch(searchCriterias.map(c => c.params))}
                  loading={isSearching}
                  disabled={isSearching || (searchCriterias.length === 0 && !(Object.keys(currentParams).length > 0 && Object.values(currentParams).some(v => v)))}
                  data-testid="execute-search-button"
                >
                  <Icon name="search" />
                  {searchCriterias.length > 0 ? 'Execute Combined Search' : 'Search'}
                </Button>

                {/* {searchCriterias.length > 0 && getAvailableSearchTypes().length > 0 && (
                  <Button
                    color="blue"
                    size="large"
                    onClick={applyAdditionalFilters}
                    style={{ marginLeft: '10px' }}
                    data-testid="apply-additional-filters-button"
                  >
                    <Icon name="filter" />
                    Apply Additional Filters
                  </Button>
                )} */}

                <Button
                  basic
                  color="red"
                  size="large"
                  onClick={clearAllFilters}
                  disabled={searchCriterias.length === 0 && !(Object.keys(currentParams).length > 0 && Object.values(currentParams).some(v => v)) && searchResults.length === 0}
                  style={{ marginLeft: '10px' }}
                  data-testid="clear-all-button"
                >
                  <Icon name="trash" />
                  Clear All Filters
                </Button>
              </Segment>
            </Grid.Column>
          </Grid.Row>
        )}

        <Grid.Row>
          <Grid.Column width={16}>
            <Segment data-testid="add-criteria-section">
              <Header as="h3">
                {/* <Icon name="plus circle" /> */}
                Add Search Criteria
              </Header>

              {isAddingAdditionalFilters && getAvailableSearchTypes().length === 0 && (
                <Message warning>
                  <Message.Header>All Search Types in Use</Message.Header>
                  <p>All available search types have been added to your filters. Execute your search or remove some filters to add different ones.</p>
                </Message>
              )}

              {isAddingAdditionalFilters && getAvailableSearchTypes().length > 0 && (
                <Message info>
                  <Message.Header>Adding Additional Filters</Message.Header>
                  <p>Select from the remaining available search types to refine your search further.</p>
                </Message>
              )}

              {/* <Form> */}
              <div className='ui form'>
              <Form.Group widths="equal">
                <Form.Select
                  label="Search Type"
                  placeholder="Select search type"
                  options={getAvailableSearchTypes()}
                  value={currentSearchType}
                  onChange={(e, { value }) => {
                    setCurrentSearchType(value as string);
                    // setCurrentParams({ discriminator: value as string });
                  }}
                  disabled={getAvailableSearchTypes().length === 0}
                  data-testid="search-type-select"
                />
                {searchCriterias.length > 0 && (
                  <Form.Select
                    label="Boolean Operator"
                    placeholder="Select operator"
                    options={BOOLEAN_OPERATORS}
                    value={currentOperator}
                    onChange={(e, { value }) => setCurrentOperator(value as 'AND' | 'OR' | 'NOT')}
                    data-testid="boolean-operator-select"
                  />
                )}
              </Form.Group>
              </div>

              <Divider />

              {renderSearchForm()}

              <Divider />

            </Segment>
          </Grid.Column>
        </Grid.Row>

        {searchResults.length === 0 && !isSearching && searchCriterias.length === 0 && (
          <BuildYourSearchQuery />
        )}

        {searchResults.length > 0 && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Segment>
                <Header as="h3">
                  Search Results
                  <Label circular color="green">{searchResults.length}</Label>
                </Header>

                <SearchResults results={searchResults} activeIndex={activeAccordion} onAccordionClick={handleAccordionClick} />
              </Segment>
            </Grid.Column>
          </Grid.Row>
        )}

      </Grid>
    </div>
  );
}


function AdvancedHeader() {
  return (<Grid.Row>
    <Grid.Column width={16}>
      <Segment>
        <Header as="h2" icon textAlign="center">
          <Icon name="search" />
          Advanced Boolean Search
          <Header.Subheader>
            Build complex search queries using multiple criteria with AND, OR, and NOT operators
          </Header.Subheader>
        </Header>
      </Segment>
    </Grid.Column>
  </Grid.Row>)
}

function BuildYourSearchQuery() {
  return (<Grid.Row>
    <Grid.Column width={16}>
      <Message info>
        <Message.Header>Build Your Search Query</Message.Header>
        <p>
          You can search in two ways: directly with form values or by building complex queries with multiple criteria and Boolean operators.
        </p>
        <List bulleted>
          <List.Item><strong>Quick Search:</strong> Enter criteria in the form above and search directly</List.Item>
          <List.Item><strong>Complex Search:</strong> Add multiple search criteria and combine with operators:</List.Item>
          <List.Item style={{
            marginLeft: '20px'
          }}><strong>AND:</strong> Results must match ALL criteria</List.Item>
          <List.Item style={{
            marginLeft: '20px'
          }}><strong>OR:</strong> Results can match ANY criteria</List.Item>
          <List.Item style={{
            marginLeft: '20px'
          }}><strong>NOT:</strong> Exclude results matching the criteria</List.Item>
        </List>
      </Message>
    </Grid.Column>
  </Grid.Row>)
}